// src/components/RichTextToolbar.jsx
// Formatierungs-Toolbar für RichTextEditor.jsx (Tiptap). Erhält die Tiptap-
// Editor-Instanz und ruft Chain-Commands auf.
// Verschoben aus src/components/research/ (ADR 0058): der Editor wird seit
// US-PEND-010 auch in Pendenzen genutzt, nicht mehr nur im Research-Tool.

import React from "react";

const Btn = ({ active, disabled, title, onClick, children }) => (
  <button
    type="button"
    className={`rte-btn${active ? " active" : ""}`}
    title={title}
    aria-pressed={!!active}
    disabled={disabled}
    // Aus der Tab-Reihenfolge nehmen: Formatierungs-Buttons werden per Maus
    // bedient; sonst müsste man sich beim Tabben (z.B. durch die Pendenzen-
    // Schnellerfassungszeile) erst durch die ganze Toolbar tabben.
    tabIndex={-1}
    // Verhindert, dass der Editor beim Klick den Fokus (und damit die Selektion) verliert.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
);

const Sep = () => <span className="rte-sep" aria-hidden="true" />;

const RichTextToolbar = ({ editor }) => {
  if (!editor) return null;

  const chain = () => editor.chain().focus();
  const currentColor = editor.getAttributes("textStyle")?.color || "#000000";

  const setLink = () => {
    const previous = editor.getAttributes("link")?.href || "";
    const url = window.prompt("Link-URL (leer lassen zum Entfernen):", previous);
    if (url === null) return; // abgebrochen
    if (url === "") {
      chain().extendMarkRange("link").unsetLink().run();
      return;
    }
    chain().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rte-toolbar" role="toolbar" aria-label="Textformatierung">
      <Btn title="Fett" active={editor.isActive("bold")} onClick={() => chain().toggleBold().run()}>
        <strong>B</strong>
      </Btn>
      <Btn title="Kursiv" active={editor.isActive("italic")} onClick={() => chain().toggleItalic().run()}>
        <em>I</em>
      </Btn>
      <Btn title="Unterstrichen" active={editor.isActive("underline")} onClick={() => chain().toggleUnderline().run()}>
        <span style={{ textDecoration: "underline" }}>U</span>
      </Btn>
      <Btn title="Durchgestrichen" active={editor.isActive("strike")} onClick={() => chain().toggleStrike().run()}>
        <span style={{ textDecoration: "line-through" }}>S</span>
      </Btn>

      <Sep />

      <Btn title="Tiefgestellt" active={editor.isActive("subscript")} onClick={() => chain().toggleSubscript().run()}>
        X<sub>2</sub>
      </Btn>
      <Btn title="Hochgestellt" active={editor.isActive("superscript")} onClick={() => chain().toggleSuperscript().run()}>
        X<sup>2</sup>
      </Btn>

      <Sep />

      <label className="rte-color" title="Textfarbe" onMouseDown={(e) => e.preventDefault()}>
        <span className="rte-color-swatch" style={{ background: currentColor }} />
        <input
          type="color"
          tabIndex={-1}
          value={currentColor}
          onChange={(e) => chain().setColor(e.target.value).run()}
        />
      </label>
      <Btn title="Farbe zurücksetzen" onClick={() => chain().unsetColor().run()}>
        A<span style={{ opacity: 0.5 }}>⊘</span>
      </Btn>

      <Sep />

      <Btn title="Linksbündig" active={editor.isActive({ textAlign: "left" })} onClick={() => chain().setTextAlign("left").run()}>
        ⬅
      </Btn>
      <Btn title="Zentriert" active={editor.isActive({ textAlign: "center" })} onClick={() => chain().setTextAlign("center").run()}>
        ↔
      </Btn>
      <Btn title="Rechtsbündig" active={editor.isActive({ textAlign: "right" })} onClick={() => chain().setTextAlign("right").run()}>
        ➡
      </Btn>

      <Sep />

      <Btn title="Aufzählung" active={editor.isActive("bulletList")} onClick={() => chain().toggleBulletList().run()}>
        • ☰
      </Btn>
      <Btn title="Nummerierte Liste" active={editor.isActive("orderedList")} onClick={() => chain().toggleOrderedList().run()}>
        1. ☰
      </Btn>

      <Sep />

      <Btn title="Link" active={editor.isActive("link")} onClick={setLink}>
        🔗
      </Btn>
    </div>
  );
};

export default RichTextToolbar;
